import { Injectable } from '@angular/core';

/**
 * Service to transfer files between different apps (e.g., homepage -> customer-management)
 * Uses IndexedDB to persist files across page navigation since sessionStorage can't handle binary File objects.
 */
@Injectable({ providedIn: 'root' })
export class FileTransferService {
    private readonly DB_NAME = 'FileTransferDB';
    private readonly STORE_NAME = 'pending_files';
    private readonly DB_VERSION = 1;

    private dbPromise: Promise<IDBDatabase> | null = null;

    private openDB(): Promise<IDBDatabase> {
        if (this.dbPromise) return this.dbPromise;

        this.dbPromise = new Promise((resolve, reject) => {
            const request = indexedDB.open(this.DB_NAME, this.DB_VERSION);

            request.onerror = () => reject(request.error);
            request.onsuccess = () => resolve(request.result);

            request.onupgradeneeded = (event) => {
                const db = (event.target as IDBOpenDBRequest).result;
                if (!db.objectStoreNames.contains(this.STORE_NAME)) {
                    db.createObjectStore(this.STORE_NAME, { keyPath: 'id', autoIncrement: true });
                }
            };
        });

        return this.dbPromise;
    }

    /**
     * Store files for transfer to another app
     */
    async storeFiles(files: File[]): Promise<void> {
        console.log('[FileTransfer] storeFiles called with', files.length, 'files:', files.map(f => f.name));
        const db = await this.openDB();
        console.log('[FileTransfer] DB opened for storing');

        // Clear existing files first
        await this.clearFiles();
        console.log('[FileTransfer] Cleared existing files');

        const tx = db.transaction(this.STORE_NAME, 'readwrite');
        const store = tx.objectStore(this.STORE_NAME);

        for (const file of files) {
            // Convert File to ArrayBuffer for storage
            const arrayBuffer = await file.arrayBuffer();
            console.log('[FileTransfer] Storing file:', file.name, 'size:', arrayBuffer.byteLength);
            store.add({
                name: file.name,
                type: file.type,
                size: file.size,
                lastModified: file.lastModified,
                data: arrayBuffer
            });
        }

        return new Promise((resolve, reject) => {
            tx.oncomplete = () => {
                console.log('[FileTransfer] Files stored successfully');
                resolve();
            };
            tx.onerror = () => {
                console.error('[FileTransfer] Error storing files:', tx.error);
                reject(tx.error);
            };
        });
    }

    /**
     * Retrieve stored files and convert back to File objects
     */
    async retrieveFiles(): Promise<File[]> {
        const db = await this.openDB();
        const tx = db.transaction(this.STORE_NAME, 'readonly');
        const store = tx.objectStore(this.STORE_NAME);
        const request = store.getAll();

        return new Promise((resolve, reject) => {
            request.onsuccess = () => {
                const records = request.result || [];
                const files = records.map((record: any) => {
                    const blob = new Blob([record.data], { type: record.type });
                    return new File([blob], record.name, {
                        type: record.type,
                        lastModified: record.lastModified
                    });
                });
                resolve(files);
            };
            request.onerror = () => reject(request.error);
        });
    }

    /**
     * Clear all stored files
     */
    async clearFiles(): Promise<void> {
        const db = await this.openDB();
        const tx = db.transaction(this.STORE_NAME, 'readwrite');
        const store = tx.objectStore(this.STORE_NAME);
        store.clear();

        return new Promise((resolve, reject) => {
            tx.oncomplete = () => resolve();
            tx.onerror = () => reject(tx.error);
        });
    }

    /**
     * Check if there are pending files to process
     */
    async hasFiles(): Promise<boolean> {
        console.log('[FileTransfer] hasFiles called');
        const db = await this.openDB();
        const tx = db.transaction(this.STORE_NAME, 'readonly');
        const store = tx.objectStore(this.STORE_NAME);
        const request = store.count();

        return new Promise((resolve, reject) => {
            request.onsuccess = () => {
                const hasFiles = request.result > 0;
                console.log('[FileTransfer] hasFiles result:', hasFiles, '(count:', request.result, ')');
                resolve(hasFiles);
            };
            request.onerror = () => {
                console.error('[FileTransfer] hasFiles error:', request.error);
                reject(request.error);
            };
        });
    }

    /**
     * Get file names without loading full file data (for display purposes)
     */
    async getFileNames(): Promise<string[]> {
        const db = await this.openDB();
        const tx = db.transaction(this.STORE_NAME, 'readonly');
        const store = tx.objectStore(this.STORE_NAME);
        const request = store.getAll();

        return new Promise((resolve, reject) => {
            request.onsuccess = () => {
                const records = request.result || [];
                resolve(records.map((r: any) => r.name));
            };
            request.onerror = () => reject(request.error);
        });
    }
}
